import React, { useCallback, useEffect, useRef, useState } from 'react';
import { isNil } from 'lodash';

import { getBoundingClientRect, useResizeObserver } from '../../../utilities';
import { ToolbarDropdown, DropdownItem } from '../ToolbarDropdownWithOverlay';

import './style.scss';

const DISPLAY = {
  ALL: 'all',
  AUTODETEDTED: 'autodetected' // TODO
};

const ToolbarButtonGroup = ({ children, disabled, displayed = DISPLAY.ALL, highlight, playerRef, overlayConfig }) => {
  const [displayItems, setDisplayItems] = useState([]);
  const [highlightItem, setHighlightItem] = useState(-1);
  const [highlightLayout, setHighlightLayout] = useState({
    x: 0,
    y: 0,
    width: 0,
    height: 0
  });
  const containerRef = useRef();
  const highlightRef = useRef();

  const buttons = React.Children.toArray(children);

  useEffect(() => {
    let items = buttons.map((_, i) => i),
      index = -1;

    if (!isNil(highlight)) {
      index = buttons.findIndex(button => button.props.name === highlight);
    }

    if (typeof displayed === 'number' && buttons.length > 0 && displayed < buttons.length) {
      items = items.splice(0, displayed);

      if (index >= displayed) {
        items = [index].concat(items).splice(0, displayed);
      }
    }

    setDisplayItems(items);
    setHighlightItem(index);
  }, [children, displayed, highlight]);

  const layoutHighlight = useCallback(() => {
    if (containerRef.current) {
      let layout = { x: 0, y: 0, width: 0, height: 0 };

      if (highlightRef.current) {
        const highlightBoundingRect = getBoundingClientRect(highlightRef.current);
        const containerBoundingRect = getBoundingClientRect(containerRef.current);

        layout = {
          x: highlightBoundingRect.x - containerBoundingRect.x,
          y: highlightBoundingRect.y - containerBoundingRect.y,
          width: highlightBoundingRect.width,
          height: highlightBoundingRect.height
        };
      }

      setHighlightLayout(layout);
    }
  }, [highlightRef.current, containerRef.current]);

  useEffect(layoutHighlight, [highlightItem]);

  useResizeObserver(playerRef.current, layoutHighlight);

  const highlightStyle = {
    display: highlight ? undefined : 'none',
    backgroundColor: 'white',
    width: 32,
    height: 32,
    borderRadius: '50%',
    top: highlightLayout.y + highlightLayout.height / 2,
    left: highlightLayout.x + highlightLayout.width / 2,
    transform: 'translate(-50%, -50%)'
  };

  const renderDisplayItems = () => (
    <div className="up-displayed-buttons" ref={containerRef}>
      {displayItems.map(index => {
        const button = buttons[index];

        return React.cloneElement(button, {
          key: button.props.name,
          ref: index === highlightItem ? highlightRef : undefined,
          className: index === highlightItem ? 'highlight' : undefined,
          disabled: disabled || button.props.disabled
        });
      })}
      <div className="up-button-group-highlight" style={highlightStyle} />
    </div>
  );

  const renderFoldedItems = () => {
    const foldedItems = buttons.filter((_, i) => !displayItems.includes(i));

    const _overlayConfig = {
      offsetY: 27,
      positions: {
        originX: 'center',
        originY: 'top',
        overlayX: 'center',
        overlayY: 'bottom',
        offsetX: 0,
        offsetY: -25
      },
      ...overlayConfig
    };

    return (
      <ToolbarDropdown overlayConfig={_overlayConfig}>
        {foldedItems.reverse().map(button => {
          const { action, icon, name } = button.props;
          return <DropdownItem key={name} action={action} icon={icon} />;
        })}
      </ToolbarDropdown>
    );
  };

  return (
    <div className="up-toolbar-button-group">
      {displayItems.length && renderDisplayItems()}
      {displayItems.length < buttons.length && renderFoldedItems()}
    </div>
  );
};

ToolbarButtonGroup.DISPLAY = DISPLAY;

export default ToolbarButtonGroup;
